/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useState, type ReactNode } from 'react'

import { Alert, AssetDetailModal, SimpleTable, TableSkeleton } from '@/components/ui'
import { useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption from '@/insights/components/Caption'
import {
  ApiService,
  AssetTypeFilter,
  type InsightDefinition,
  type InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import { downloadCsv } from '@/utils/csvExport'
import { formatUserDate } from '@/utils/formatDate'

export interface CurrentEngineHoursParams extends InsightParametersType {
  companyId?: number
  assetIds?: string[]
}

export interface CurrentEngineHourRecord {
  assetId: number
  assetName: string
  fleetId: string | null
  electricHours: string | null
  dieselHours: string | null
  totalHours: string | null
  fridgeEngineHoursSampleTime: string | Date | null
}

const PATH = '/insights/reports/current-engine-hours'

const HEADERS = ['Name', 'Fleet Id', 'Electric Hours', 'Diesel Hours', 'Total Hours', 'Engine Hours Sample Time']

const CurrentEngineHours: InsightDefinition<CurrentEngineHoursParams, CurrentEngineHourRecord[]> = {
  active: true,
  slug: 'asset/current-engine-hours',
  category: 'asset',
  name: 'Current Engine Hours',
  description: 'Display the latest electric, diesel and total engine hours for each asset',
  defaultWidth: 'full',
  defaultAspectRatio: 16 / 9,

  configuration: {
    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      {
        id: 'assetIds',
        control: 'AssetDropdownMulti',
        required: true,
        includeSelectAll: true,
        assetType: AssetTypeFilter.InTransit,
      },
    ],

    isValid: (configuration: CurrentEngineHoursParams) => {
      if (configuration?.companyId && configuration.assetIds?.length) {
        return true
      }
      return 'Company and at least one Asset are required.'
    },
  },

  load: async (api: ApiService, parameters: CurrentEngineHoursParams) => {
    const path = `${PATH}?${buildQuery(parameters)}`
    const response = await api.get<CurrentEngineHourRecord[]>({ path, origin: 'CurrentEngineHours' })
    if (response.error) {
      return { code: InsightStatusCode.ERROR, message: response.message }
    }
    if (!response.data?.length) {
      return { code: InsightStatusCode.NO_DATA }
    }
    return response.data
  },

  csv: async (api: ApiService, parameters: CurrentEngineHoursParams, title: string) => {
    await downloadCsv(api, PATH, parameters, title)
  },

  Display: ({ status, data, configuration }) => {
    const [selectedAsset, setSelectedAsset] = useState<number | undefined>()
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })

    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    if (showSkeleton) {
      return <TableSkeleton columns={HEADERS.length} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      // Wait until we're ready to display; the framework handles error and no-data.
      return null
    }
    if (isInsightStatus(data)) {
      return null
    }
    if (!data?.length) {
      return null
    }

    const parameters = configuration.parameters as CurrentEngineHoursParams
    const body = data.map(
      (record): Record<string, ReactNode> => ({
        Name: (
          <button
            onClick={() => setSelectedAsset(record.assetId)}
            className="text-left block text-blue-500 cursor-pointer"
          >
            {record.assetName ?? '-'}
          </button>
        ),
        'Fleet Id': record.fleetId ?? '-',
        'Electric Hours': record.electricHours ?? '-',
        'Diesel Hours': record.dieselHours ?? '-',
        'Total Hours': record.totalHours ?? '-',
        'Engine Hours Sample Time': formatUserDate(record.fridgeEngineHoursSampleTime),
      })
    )

    return (
      <section className="text-base-content text-base h-full flex flex-col">
        <div className="min-h-0 flex-1">
          <SimpleTable headers={HEADERS} body={body} cellClassNames={{ 'Engine Hours Sample Time': 'text-nowrap' }} />
        </div>
        <AssetDetailModal
          show={selectedAsset !== undefined}
          onHide={() => setSelectedAsset(undefined)}
          assetId={selectedAsset}
          companyId={parameters.companyId}
        />
      </section>
    )
  },

  Caption: ({ configuration, status }) => {
    const { companies } = useCompany()
    const params = configuration.parameters as CurrentEngineHoursParams

    if (status.code === InsightStatusCode.ERROR) {
      // Framework will handle this
      return ''
    }
    const fields: Record<string, string> = {}
    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) {
        fields['Company'] = company.name
      }
    }
    if (params.assetIds?.length) {
      fields['Assets'] = `${params.assetIds.length} Asset${params.assetIds.length === 1 ? '' : 's'}`
    }
    if (Object.keys(fields).length) {
      return <Caption fields={fields} />
    }
    return ''
  },

  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING || (status.code === InsightStatusCode.ERROR && data === undefined)) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No records found</div>
    }
    const rows = data?.length
    if (!rows) {
      return <div>No records found</div>
    }
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default CurrentEngineHours
